/**
 * The Excel-like header row, its filter dropdown, and the toolbar above it.
 *
 * See `lib/sheet.ts` for why the body is left alone. These render only the
 * `<thead>` and the controls around the table.
 *
 * Every affordance carries a glyph as well as a colour or a position — an
 * active filter is a funnel with a count, not a tinted heading (non-negotiable
 * 9). And the sort state goes on `aria-sort`, so a screen reader announces it
 * rather than the arrow being decoration only sighted users can read.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { SheetState } from "../lib/sheet";
import { BLANK, exportSheet } from "../lib/sheet";
import { Info } from "./Info";

interface HeadProps<T> {
  sheet: SheetState<T>;
  /** Rendered after the generated headings — a row-actions column heading. */
  trailing?: React.ReactNode;
}

export function SheetHead<T>({ sheet, trailing }: HeadProps<T>) {
  const [openKey, setOpenKey] = useState<string | null>(null);

  return (
    <thead className="sheet__head">
      <tr>
        {sheet.columns.map((column) => {
          const sorted = sheet.sortKey === column.key ? sheet.sortDirection : null;
          const filtered = Boolean(sheet.filters[column.key]);
          const sortable = column.sortable !== false;
          const filterable = column.filterable !== false;

          return (
            <th
              key={column.key}
              className={`sheet__th${column.numeric ? " num" : ""}`}
              aria-sort={
                sorted === "asc"
                  ? "ascending"
                  : sorted === "desc"
                    ? "descending"
                    : "none"
              }
            >
              <span className="sheet__th-inner">
                {sortable ? (
                  <button
                    type="button"
                    className="sheet__sort"
                    onClick={() => sheet.toggleSort(column.key)}
                    title={
                      sorted === "asc"
                        ? "Sorted A–Z. Click for Z–A."
                        : sorted === "desc"
                          ? "Sorted Z–A. Click to restore the original order."
                          : `Sort by ${column.label}`
                    }
                  >
                    <span>{column.label}</span>
                    <span className="sheet__arrow" aria-hidden="true">
                      {sorted === "asc" ? "▲" : sorted === "desc" ? "▼" : "⇅"}
                    </span>
                  </button>
                ) : (
                  <span className="sheet__label">{column.label}</span>
                )}
                <Info text={column.info} />

                {filterable && (
                  <FilterButton
                    sheet={sheet}
                    columnKey={column.key}
                    label={column.label}
                    active={filtered}
                    open={openKey === column.key}
                    onToggle={() =>
                      setOpenKey((k) => (k === column.key ? null : column.key))
                    }
                    onClose={() => setOpenKey(null)}
                  />
                )}
              </span>
            </th>
          );
        })}
        {trailing}
      </tr>
    </thead>
  );
}

interface FilterProps<T> {
  sheet: SheetState<T>;
  columnKey: string;
  label: string;
  active: boolean;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}

function FilterButton<T>({
  sheet,
  columnKey,
  label,
  active,
  open,
  onToggle,
  onClose,
}: FilterProps<T>) {
  const panelId = useId();
  const wrapper = useRef<HTMLSpanElement>(null);
  const [query, setQuery] = useState("");

  // Working copy: nothing is applied until Apply is pressed, so ticking
  // through twenty values does not re-render the table twenty times, and
  // Cancel means cancel.
  const options = useMemo(
    () => (open ? sheet.optionsFor(columnKey) : []),
    [open, sheet, columnKey]
  );
  const [chosen, setChosen] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    const existing = sheet.filters[columnKey];
    setChosen(new Set(existing ?? sheet.optionsFor(columnKey)));
    setQuery("");
    // Only when the panel opens: re-syncing on every keystroke would undo
    // the ticks the person is making.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, columnKey]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const searching = query.trim().length > 0;
  const shown = searching
    ? options.filter((o) => o.toLowerCase().includes(query.trim().toLowerCase()))
    : options;
  const allShownChosen = shown.length > 0 && shown.every((o) => chosen.has(o));

  /**
   * What Apply will commit.
   *
   * While a search is active this is the ticked values *among the matches* —
   * which is what Excel does and what anybody who has typed into that box
   * expects. Taking the whole ticked set instead makes the search purely
   * cosmetic: everything starts ticked, so searching and pressing Apply
   * filtered nothing at all.
   */
  const effective = searching
    ? new Set(shown.filter((o) => chosen.has(o)))
    : chosen;

  function apply() {
    // Everything ticked is the same as no filter — store it that way so the
    // funnel does not claim a filter that hides nothing.
    sheet.setFilter(
      columnKey,
      effective.size === options.length ? null : new Set(effective)
    );
    onClose();
  }

  return (
    <span className="sheet__filterwrap" ref={wrapper}>
      <button
        type="button"
        className={`sheet__filter${active ? " sheet__filter--on" : ""}`}
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={
          active ? `${label}: filtered. Change the filter.` : `Filter ${label}`
        }
        title={active ? `${label} is filtered` : `Filter ${label}`}
      >
        {active ? "▼●" : "▼"}
      </button>

      {open && (
        <div className="sheet__panel" id={panelId} role="dialog"
             aria-label={`Filter ${label}`}>
          <input
            className="sheet__search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${label.toLowerCase()}`}
            autoFocus
          />

          <label className="sheet__option sheet__option--all">
            <input
              type="checkbox"
              checked={allShownChosen}
              onChange={(e) => {
                const next = new Set(chosen);
                for (const option of shown) {
                  if (e.target.checked) next.add(option);
                  else next.delete(option);
                }
                setChosen(next);
              }}
            />
            <span>
              {searching ? "Select all matching" : "(Select all)"}
              <span className="sheet__count">{shown.length}</span>
            </span>
          </label>

          <div className="sheet__options">
            {shown.length === 0 && (
              <p className="sheet__empty">Nothing matches “{query}”.</p>
            )}
            {searching && shown.length > 0 && (
              <p className="sheet__note">
                Applying keeps only what is ticked here.
              </p>
            )}
            {shown.map((option) => (
              <label key={option} className="sheet__option">
                <input
                  type="checkbox"
                  checked={chosen.has(option)}
                  onChange={(e) => {
                    const next = new Set(chosen);
                    if (e.target.checked) next.add(option);
                    else next.delete(option);
                    setChosen(next);
                  }}
                />
                <span className={option === BLANK ? "sheet__blank" : undefined}>
                  {option}
                </span>
              </label>
            ))}
          </div>

          <div className="sheet__panel-foot">
            <button
              type="button"
              className="btn btn--ghost btn--auto btn--sm"
              onClick={() => {
                sheet.setFilter(columnKey, null);
                onClose();
              }}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn btn--auto btn--sm"
              onClick={apply}
              disabled={effective.size === 0}
              title={
                effective.size === 0
                  ? "Nothing is ticked, which would hide every row"
                  : undefined
              }
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </span>
  );
}

interface ToolbarProps<T> {
  sheet: SheetState<T>;
  /** Base name for the exported file; the date is appended. */
  exportName: string;
  /** Shown to the left of the controls — a title or a note. */
  children?: React.ReactNode;
}

/**
 * Search, the filtered-row count, Clear, and Export.
 *
 * Every table exports (non-negotiable 11), and every table says how many rows
 * it is hiding — a filtered table that looks like an unfiltered one is how
 * people come to distrust a report.
 */
export function SheetToolbar<T>({ sheet, exportName, children }: ToolbarProps<T>) {
  const hidden = sheet.total - sheet.rows.length;

  return (
    <div className="sheet__toolbar">
      <div className="sheet__toolbar-left">{children}</div>

      <div className="sheet__toolbar-right">
        {hidden > 0 && (
          <span className="pill pill--warning" role="status">
            ▼ {sheet.rows.length.toLocaleString("en-IN")} of{" "}
            {sheet.total.toLocaleString("en-IN")}
          </span>
        )}
        <input
          className="sheet__find"
          value={sheet.search}
          onChange={(e) => sheet.setSearch(e.target.value)}
          placeholder="Find in table"
          aria-label="Find in table"
        />
        {sheet.activeFilterCount > 0 && (
          <button
            type="button"
            className="btn btn--ghost btn--auto btn--sm"
            onClick={sheet.clearFilters}
          >
            Clear filters
          </button>
        )}
        <button
          type="button"
          className="btn btn--ghost btn--auto btn--sm"
          onClick={() =>
            exportSheet(sheet,
                        `${exportName}-${new Date().toISOString().slice(0, 10)}`)
          }
          disabled={sheet.rows.length === 0}
          title="Download what is on screen, filtered and sorted as it is"
        >
          Export
        </button>
      </div>
    </div>
  );
}
